import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";
import useAutoRetry from "../hooks/useAutoRetry";
import { IS_PREVIEW } from "../config";
import syncDelivery from "./deliverySync";
import { recordFailure, setProbe } from "./syncDiagnostics";
import {
  adoptLegacyProgress,
  completeLesson,
  getAllProgress,
  markSynced,
  needsSync,
  pendingEntries,
  progressOwner,
  setStep,
  startLesson,
  teachAgain,
} from "./progressStore";

// Owns the teacher's local progress in the class they are teaching now, and
// keeps the server in step behind it for every class they teach. Nothing here
// is ever awaited by a screen: teaching continues at local speed.
export const ProgressContext = createContext(null);

export const ProgressProvider = ({ classroomId, classroomIds, adoptLegacy, children }) => {
  const { user } = useAuth();
  const userId = user.id;
  const owner = progressOwner(userId, classroomId);
  const [all, setAll] = useState(() => {
    if (adoptLegacy) adoptLegacyProgress(userId, owner);
    return getAllProgress(owner);
  });
  const inFlight = useRef(new Set());

  const refresh = useCallback(() => setAll(getAllProgress(owner)), [owner]);

  // A delivery always goes to the class it was taught in, even after the
  // teacher has switched to another.
  const classKey = classroomIds.join(",");
  const pending = useCallback(
    () =>
      classKey
        .split(",")
        .flatMap((id) =>
          pendingEntries(progressOwner(userId, id)).map((entry) => [id, ...entry])
        ),
    [userId, classKey]
  );

  const push = useCallback(async () => {
    // Preview runs on a local fixture with no server to reach.
    if (IS_PREVIEW) return;
    for (const [recordClassroomId, lessonId, record] of pending()) {
      // Keyed by delivery: a finished and a new delivery of the same lesson
      // can both be waiting.
      if (inFlight.current.has(record.syncKey)) continue;
      inFlight.current.add(record.syncKey);
      try {
        await syncDelivery({
          record,
          lessonId,
          teacherId: userId,
          classroomId: recordClassroomId,
        });
        markSynced(progressOwner(userId, recordClassroomId), lessonId, record.rev, record.syncKey);
        refresh();
      } catch (error) {
        // Kept on this device and tried again later. A teacher is never shown
        // a failed request and is never stopped by one; the reason is only
        // recorded where a developer can go and look for it.
        recordFailure(lessonId, error);
      } finally {
        inFlight.current.delete(record.syncKey);
      }
    }
  }, [userId, pending, refresh]);

  // Runs on sign-in — clearing anything left over from a previous session —
  // and after every teacher action.
  useEffect(() => {
    push();
  }, [push, all]);

  // `all` only holds current deliveries; a replaced one may still be waiting.
  const waiting =
    !IS_PREVIEW && (Object.values(all).some(needsSync) || pending().length > 0);
  useAutoRetry(waiting, push);

  // Lets a developer see whether the loop has anything to send at all: a
  // lesson carrying no syncKey was last touched before deliveries existed and
  // will only gain one when the teacher opens it again.
  useEffect(() => {
    setProbe(() => ({
      mode: IS_PREVIEW ? "preview" : "live",
      teacher: userId,
      classroom: classroomId,
      lessons: Object.entries(getAllProgress(owner)).map(([id, record]) => ({
        lesson: id,
        status: record.status,
        rev: record.rev ?? null,
        syncedRev: record.syncedRev ?? null,
        hasSyncKey: Boolean(record.syncKey),
        needsSync: needsSync(record),
      })),
    }));
  }, [userId, classroomId, owner, all]);

  const bind = useCallback(
    (action) =>
      (lessonId, ...args) => {
        const result = action(owner, lessonId, ...args);
        refresh();
        return result;
      },
    [owner, refresh]
  );

  const value = useMemo(
    () => ({
      get: (lessonId) => all[lessonId] || null,
      start: bind(startLesson),
      teachAgain: bind(teachAgain),
      setStep: bind(setStep),
      complete: bind(completeLesson),
    }),
    [all, bind]
  );

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};
